import { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import { Prefectures, Status } from "../../assets/data/Constants";
import authorityUserType from "../../assets/data/TkAuthorityProperty";
import { GlobalContext } from "../../context/globalContext";
import { BusinessContext } from "../../context/businessContext";
import TkFakeTable from "../../components/molecules/TkFakeTable";
import TkDataTable from "../../components/molecules/TkDataTable";
import TkBackButton from "../../components/molecules/TkBackButton";
import TkDateDialog from "../../components/molecules/TkDateDialog";
import TkMessageDialog from "../../components/molecules/TkMessageDialog";
import { TkGetCode } from "../../utils/TkGetCode";
import { ITkDataTableBodyProps } from "../../type/componentsType/TkDataTable";
import { IAPI_FAC013_Request } from "../../type/apiType";
import { FacilityList } from "../../type/apiType/IAPI_FAC001";
import { FacilityList as CancelFacilityList } from "../../type/apiType/IAPI_FAC013";
import { httpIAPI_FAC001, httpIAPI_FAC013 } from "./api/request";
import { fakeTableList, infoHeadTitleList, schema } from "./parts";
import "./_style.scss";
import _ from "lodash";
import TkRadio from "../../components/atoms/TkRadio";

/**
 * SCRC050019 非加入の取下げ登録
 * @return SCRC050019
 */
const SCRC050019 = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { state, setState } = useContext(GlobalContext);
  const { state: businessState } = useContext(BusinessContext);
  const userType = state.authInfo?.userType;
  const isUserTypeKiko = userType === authorityUserType.userTypeNumber.KIKO;
  const contractorNo = isUserTypeKiko
    ? businessState.common?.contractorNo!
    : state.authInfo?.contractorNo!;

  const [stateInfoList, setStateInfoList] = useState<any>([]);
  const stateFakeTableList = useRef(_.cloneDeep(fakeTableList));

  const [facilityData, setFacilityData] = useState<FacilityList[]>([]);
  const [isOpenKikoDialog, setIsOpenKikoDialog] = useState<boolean>(false);
  const [isOpenDialog, setIsOpenDialog] = useState<boolean>(false);
  const [selectedFacilityNo, setSelectedFacilityNo] = useState<string>("");
  const [cancelDate, setCancelDate] = useState<string>("");
  // 活性非活性
  const [needDisable, setNeedDisable] = useState<boolean>(false);

  const handleCallApiInit = async () => {
    // 「施設を転換後加入させない旨の届」の取り下げ対象一覧参照API 呼び出し
    try {
      const res = await httpIAPI_FAC001({
        contractorNo: contractorNo,
        applyId: location.state?.applyId,
      });

      if (res.resultCd === "I0000") {
        let resArr = stateFakeTableList.current;
        let cancelDate = resArr.find((item) => item.keyword === "cancelDate")!;

        const excludedStatuses = [Status["届出待ち"], Status["差戻し一時保存"]];
        const isApproving = !excludedStatuses.includes(
          res.latestApplyStatus as Status
        );

        setNeedDisable(isApproving);
        setCancelDate(res.joinDate ?? "");
        cancelDate.disabled = isApproving;
        cancelDate.setDefaultValue = res.joinDate ?? "";

        setStateInfoList([...resArr]);
        setFacilityData(res.facilityList!);
        setState({
          ...state,
          errorMsgId: "",
        });
      } else {
        setState({
          ...state,
          errorMsgId: res.resultCd,
        });
      }
    } catch {
      navigate("/error");
    }
  };

  // 初期表示
  useEffect(() => {
    let resArr = stateFakeTableList.current;
    let contractorName = resArr.find(
      (item) => item.keyword === "contractorName"
    )!;

    // 契約者（法人）名
    contractorName.showLine = isUserTypeKiko;
    contractorName.contractorNumber =
      contractorNo + ":" + businessState.common?.contractorName;

    setStateInfoList([...resArr]);

    handleCallApiInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let resArr = stateFakeTableList.current;
    let facilityList = resArr.find((item) => item.keyword === "facilityList")!;
    const bodyList: ITkDataTableBodyProps[][] = facilityData?.map((item) => {
      const prefectureName = TkGetCode(
        Prefectures,
        item.facilityPrefectureCd ?? ""
      );
      return [
        {
          children: (
            <Box className="TkRadioInSCRC050019">
              <TkRadio
                checked={needDisable || selectedFacilityNo === item.facilityNo}
                disabled={needDisable}
                onClick={() => {
                  setSelectedFacilityNo((prevSelectedFacilityNo) => {
                    if (prevSelectedFacilityNo === item.facilityNo) {
                      return "";
                    } else {
                      return item.facilityNo!;
                    }
                  });
                }}
              />
            </Box>
          ),
          width: "60px",
        },
        {
          children: (item.facilityNo ?? "") + ":" + (item.facilityName ?? ""),
          forSort: item.facilityNo,
          width: 150,
        },
        {
          children: item.facilityKind,
          forSort: item.facilityKind,
          width: 200,
        },
        {
          children:
            prefectureName +
            (item.facilityAddressCity ?? "") +
            (item.facilityAddressOthers ?? ""),
          forSort: `${prefectureName}${item.facilityAddressCity}${item.facilityAddressOthers}`,
          width: 250,
        },
        {
          children: item.contractStatusName,
          forSort: item.contractStatusName,
          width: 150,
        },
      ];
    })!;

    facilityList.node = (
      <Box className="node">
        <TkDataTable
          checkSection={false}
          checkedAll={false}
          bodyList={bodyList}
          headList={infoHeadTitleList}
        />
      </Box>
    );
    setStateInfoList([...resArr]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityData, selectedFacilityNo]);

  const onCheckAndSubmit = (data: any) => {
    // 必須チェック
    if (selectedFacilityNo === "") {
      setState({ ...state, errorMsgId: "TKMSG_SCR0094E" });
      return;
    } else {
      setState({ ...state, errorMsgId: "" });
    }

    setCancelDate(data.cancelDate);

    if (isUserTypeKiko) {
      setIsOpenKikoDialog(true);
    } else {
      setIsOpenDialog(true);
    }
  };

  const makeHttpObj = (receptionDate?: any): IAPI_FAC013_Request => {
    const facilityList: CancelFacilityList[] = facilityData
      .map((item) => {
        return {
          cancelFacilityId: item.facilityNo,
          cancelDate: cancelDate,
          contractStatus: item.contractStatus,
        };
      })
      .filter((filterItem) =>
        selectedFacilityNo.includes(filterItem.cancelFacilityId!)
      );

    const reqObj: IAPI_FAC013_Request = {
      contractorNo: contractorNo,
      facilityList: facilityList,
      receptionDate: receptionDate,
      screenId: "SCRC050019",
    };

    return reqObj;
  };

  const handleSubmit = async (date?: any) => {
    // 施設を転換後加入させない旨の取下届更新API 呼び出し
    try {
      const res = await httpIAPI_FAC013({
        ...makeHttpObj(date),
      });

      if (res.resultCd === "I0000") {
        setState({
          ...state,
          errorMsgId: "",
        });
        navigate("/SCRC070008");
      } else {
        setState({
          ...state,
          errorMsgId: res.resultCd,
        });
      }
    } catch {
      navigate("/error");
    }
  };

  return (
    <Box className="SCRC050019" data-testid="SCRC050019PageForTest">
      <TkFakeTable
        list={stateInfoList}
        ruleSchema={schema}
        showButton={false}
        buttonGroup={<TkBackButton />}
        submitButtonGroup={[
          {
            submitButtonText: "取下げ",
            submitButtonDisabled: needDisable,
            showButton: !needDisable,
          },
        ]}
        onCheckAndSubmit={onCheckAndSubmit}
      />

      <TkMessageDialog
        open={isOpenDialog}
        onOky={handleSubmit}
        onCancel={() => setIsOpenDialog(false)}
        title="確認（機構へ取り下げます）"
        contextText="　機構へ取り下げを行いますが、問題ないでしょうか。"
        cancelText="いいえ"
        okText="はい"
      />

      <TkDateDialog
        open={isOpenKikoDialog}
        onOky={handleSubmit}
        onCancel={() => setIsOpenKikoDialog(false)}
        minDate={new Date("1900/01/01")}
        maxDate={new Date()}
        title="確認（申請を行います）"
        contextText="　申請を行いますが、問題ないでしょうか。"
        cancelText="いいえ"
        okText="はい"
      />
    </Box>
  );
};

export default SCRC050019;
